import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Skeleton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	useGetOffers,
	useMonitoringOffersStore,
	useSpecialistsByIds,
} from 'services/monitoring-offers';
import MonitoringCard from '../components/Card/MonitoringCard';
import { Empty } from '../components/Empty';

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxShadow: 3,
		position: 'relative',
		bgcolor: 'white',
		borderRadius: 3,
	},
};

export const MonitoringOfferPage = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const size = useWindowSize();

	usePageTitle(t('offers'));
	useAppbarTitle('offers');

	const { isLoading } = useGetOffers('request-and-offer');
	const { ongoingOffers, setSpecialists, setSpecialistsLoading } = useMonitoringOffersStore();
	const { fetchSpecialistsByIds } = useSpecialistsByIds();
	const [selectedSpecialistLoading, setSelectedSpecialistLoading] = useState(null);

	const handleSelectSpecialists = async (data: any) => {
		// Handle specialist_id as an array
		const specialistIds = Array.isArray(data?.specialist_id)
			? data.specialist_id
			: typeof data?.specialist_id === 'string'
				? [data.specialist_id]
				: [];

		setSelectedSpecialistLoading(data?.id);
		try {
			setSpecialistsLoading(true);
			localStorage.setItem('request_id', data?.id);
			const response = await fetchSpecialistsByIds(specialistIds);
			setSpecialists(response);
		} catch (error) {
			console.error('Error fetching specialists:', error);
			setSelectedSpecialistLoading(null);
		} finally {
			setSpecialistsLoading(false);
			navigateWithLanguage(`/farmer/monitoring/offers/${data?.id}/specialists`);
			setSelectedSpecialistLoading(null);
		}
	};

	return (
		<Box sx={{ ...styles.container, minHeight: size.width > 768 ? '100%' : 'auto' }}>
			<div className="flex justify-center items-center">
				<div className="absolute left-[20px] top-[50px] md:top-[20px]">
					<Button
						className="w-[120px]"
						onClick={() => navigateWithLanguage('/farmer/view-offers')}
						variant="text"
						sx={{ backgroundColor: 'white', color: 'black' }}
						startIcon={<Icon path={mdiArrowLeft} size={1} />}
					>
						{t('previous')}
					</Button>
				</div>
				<h2 className="md:md:text-2xl font-bold mb-6 mt-5">
					{t('requestsAndOffers.viewRequestsAndOffers')}
				</h2>
			</div>
			<div className="p-[20px]">
				{isLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{Array.from({ length: 6 }).map((_, index) => (
							<Box key={index} sx={{ padding: 2, boxShadow: 3, borderRadius: 3, bgcolor: 'white' }}>
								<Skeleton variant="rectangular" height={150} />
								<Skeleton variant="text" sx={{ marginTop: 1, height: 30 }} />
								<Skeleton variant="text" width="80%" />
							</Box>
						))}
					</div>
				) : ongoingOffers?.length === 0 ? (
					<Empty
						title={t('requestsAndOffers.noRequestsAndOffersYet')}
						buttonTitle={t('requestsAndOffers.ongoingProjects')}
						route="/farmer/monitoring/ongoing-projects"
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{ongoingOffers?.map((data: any, index: number) =>
							data?.id ? (
								<MonitoringCard
									key={data.id}
									data={data}
									loading={selectedSpecialistLoading}
									onSelectSpecialists={() => handleSelectSpecialists(data)}
								/>
							) : (
								<div key={index}>Loading...</div>
							)
						)}
					</div>
				)}
			</div>
		</Box>
	);
};
